// Re-exported here rather than behind a subpath export: this file is the
// package's single entry point (see package.json "exports"), and both apps
// already import from "@campushubai/shared" with no subpath. Adding one would
// mean touching module resolution in two bundlers to gain nothing.
//
// The `.js` extensions are load-bearing, not a style choice. This package is
// ESM and `tsc` emits these specifiers verbatim; Node's ESM resolver does not
// guess extensions, so an extensionless `export *` compiles fine and then
// fails at runtime in the production image. TypeScript, tsx and Vite all
// resolve `./x.js` back to `./x.ts`, so the extension costs the dev paths
// nothing. Do not strip them.
export * from "../transport/eta.js";
export * from "../avatar/permissions.js";
export * from "../diary/status.js";
export * from "../meetings/topics.js";
// Exam computation engine (grade, pass policy, totals, dedupe, matrix, rank) —
// the one implementation behind API `lib/grade-scale.ts` / `report-card-rows.ts`
// and web `lib/grade-scale.ts` / `grade-aggregate.ts`. See ../exams/grading.ts.
export * from "../exams/grading.js";
export * from "../exams/aggregate.js";
export * from "../exams/rank.js";
export * from "../exams/scheme.js";
export * from "../exams/paper-sittings.js";
// Client-onboarding checklist (ByteGo field-ops tool): phases, steps, how-to
// text and the progress engine shared by the API, /client-onboarding and the
// Control Center read-only views. See ../onboarding/types.ts.
export * from "../onboarding/index.js";

// ─── Core Enums ──────────────────────────────────────────────────────────────
//
// `UserRole` is the single source of truth for user roles across the api,
// the web app, and the seed scripts. It is a string-valued enum so values
// round-trip cleanly through Prisma (where `role` is stored as a `String`).
//
// `UserRoleValue` exposes the same set as a string union for places that
// prefer literal-string ergonomics (e.g. discriminating on `req.user.role`).

export enum UserRole {
  // Platform-owner tier — lives on control.bytego.ai and operates across
  // every tenant. Above CHAIRMAN in the role hierarchy. User.schoolId
  // stays null and the JWT carries schoolId=null (see lib/multi-school.ts).
  CONTROL_CENTER = "CONTROL_CENTER",
  CHAIRMAN = "CHAIRMAN",
  ADMIN = "ADMIN",
  DIRECTOR = "DIRECTOR",
  PRINCIPAL = "PRINCIPAL",
  TEACHER = "TEACHER",
  STUDENT = "STUDENT",
  PARENT = "PARENT",
  STAFF = "STAFF",
  // Driver — narrow-scope role for the live transport module. Drivers
  // log into the /drive app shell and only have write access to the
  // telemetry + trip-lifecycle endpoints. Added 2026-05-28.
  DRIVER = "DRIVER",
  // Kiosk — dedicated account for the entrance attendance tablet.
  // Can only reach /attendance/kiosk; every other route redirects back.
  // Admin creates kiosk credentials from Settings → Attendance.
  KIOSK = "KIOSK",
}

export type UserRoleValue = `${UserRole}`;

export enum AcademicStatus {
  ACTIVE = "ACTIVE",
  INACTIVE = "INACTIVE",
  GRADUATED = "GRADUATED",
  SUSPENDED = "SUSPENDED",
}

// ─── Core Interfaces ──────────────────────────────────────────────────────────

export interface School {
  id: string;
  name: string;
  slug: string;
  logoUrl?: string;
  address: Address;
  contactEmail: string;
  contactPhone: string;
  website?: string;
  timezone: string;
  locale: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Address {
  street: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

export interface User {
  id: string;
  schoolId: string;
  email: string;
  firstName: string;
  lastName: string;
  displayName: string;
  avatarUrl?: string;
  role: UserRole;
  isActive: boolean;
  lastLoginAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Student {
  id: string;
  userId: string;
  schoolId: string;
  studentCode: string;
  enrollmentDate: string;
  grade: string;
  section: string;
  dateOfBirth: string;
  gender: "MALE" | "FEMALE" | "OTHER";
  parentIds: string[];
  status: AcademicStatus;
  user: User;
  createdAt: string;
  updatedAt: string;
}

export interface Teacher {
  id: string;
  userId: string;
  schoolId: string;
  employeeCode: string;
  subjects: string[];
  qualifications: string[];
  joiningDate: string;
  user: User;
  createdAt: string;
  updatedAt: string;
}

export interface Parent {
  id: string;
  userId: string;
  schoolId: string;
  studentIds: string[];
  relationship: "MOTHER" | "FATHER" | "GUARDIAN";
  occupation?: string;
  user: User;
  createdAt: string;
  updatedAt: string;
}

// ─── API Response Wrapper ─────────────────────────────────────────────────────

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  timestamp: string;
}

export interface ApiError {
  success: false;
  error: {
    code: string;
    message: string;
    details?: Record<string, unknown>;
  };
  timestamp: string;
}

export interface PaginatedResponse<T> {
  success: boolean;
  data: T[];
  pagination: {
    page: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
  timestamp: string;
}

// ─── Auth Types ───────────────────────────────────────────────────────────────

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

export interface LoginPayload {
  email: string;
  password: string;
  schoolSlug?: string;
}

export interface AuthUser extends User {
  school: School;
  permissions: string[];
}

// ─── Academic Year ────────────────────────────────────────────────────────────
//
// ONE definition of the canonical academic-year label, shared by the web app
// and the API. Four divergent inline copies used to exist (June cutoff in
// settings/classes-setup and the student importer, April in admissions and
// udise), and the field was persisted as free text — so a label like
// "2026-2027" could be written but never read back, because every read is an
// exact-string match and every picker only offers `YYYY-YY`. Import from here
// rather than re-deriving locally.

/** Canonical label shape, e.g. "2026-27". */
export const ACADEMIC_YEAR_PATTERN = /^\d{4}-\d{2}$/;

/** True for a canonical label like "2026-27". */
export function isCanonicalAcademicYear(v: unknown): v is string {
  return typeof v === "string" && ACADEMIC_YEAR_PATTERN.test(v);
}

/**
 * Canonical label for a date, e.g. "2026-27". The Indian school year starts
 * in June, so anything from 1 June belongs to the year that starts that
 * calendar year. This is the cutoff the DB is labelled with — do not fork it
 * for display or intake purposes; format at the edge instead.
 */
export function deriveAcademicYear(d: Date = new Date()): string {
  const startYear = d.getMonth() >= 5 ? d.getFullYear() : d.getFullYear() - 1;
  return `${startYear}-${String(startYear + 1).slice(-2)}`;
}

/**
 * Label for the year an ADMISSION is being taken for, e.g. "2026-27".
 *
 * Deliberately a different cutoff from `deriveAcademicYear`: intake for the
 * next school year opens in April, two months before that year starts, so an
 * application filed in April/May belongs to the year that is about to begin —
 * not the one about to end. This is the one legitimate divergence; it lives
 * here, named, so it stops being re-derived inline and mistaken for a bug.
 * Everything that labels or filters school DATA must use
 * `deriveAcademicYear`.
 */
export function deriveAdmissionYear(d: Date = new Date()): string {
  const startYear = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
  return `${startYear}-${String(startYear + 1).slice(-2)}`;
}

// ── Fee heads ────────────────────────────────────────────────────────────────
// Moved to its own leaf module, ../fees/fee-heads.ts (2026-09-28) — see that
// file for the full note. Re-exported here so every existing
// `import { FEE_HEADS } from "@campushubai/shared"` keeps working unchanged.
export * from "../fees/fee-heads.js";
export * from "../fees/apply-to.js";
export * from "../fees/boarding-name-fixtures.js";
export * from "../fees/head-breakdown.js";
export * from "../fees/head-lines.js";
export * from "../fees/payment-reference.js";
export * from "../fees/installment-template.js";
export * from "../fees/lists.js";

// ── School profile ───────────────────────────────────────────────────────────
// The signup/settings contract, shared so the API and the web forms validate
// identically. See school-profile.ts.
export * from "./school-profile.js";
export * from "./entitlements.js";

// School website — template ids, content schema and the public payload shape,
// shared by the API, the admin editor and the static sites. See school-website.ts.
export * from "./school-website.js";
